import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>Privacy Policy</h1>
        <p className="text-slate-600 mb-8">Last updated: March 2026</p>

        <div className="prose prose-slate max-w-none">
          {/* Important Notice */}
          <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-6 mb-8">
            <p className="text-indigo-800 font-medium mb-0">
              Sponsiq analyzes publicly available creator content and does not access private YouTube account data. 
              Your privacy is important to us, and we are committed to protecting your personal information.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. Information We Collect</h2>
            <p className="text-slate-700 mb-4">We collect information in the following ways:</p>
            
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Account Information</h3>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>Email address (required for account creation)</li>
              <li>Name (if provided)</li>
              <li>Profile information from social authentication providers</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-800 mb-2">Creator Data</h3>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>YouTube channel information (channel name, subscriber count, video count)</li>
              <li>Publicly available video metadata (titles, descriptions, view counts)</li>
              <li>Detected niche and content categories</li>
              <li>Sponsorship readiness scores and brand match recommendations</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-800 mb-2">Usage Analytics</h3>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Pages visited and features used</li>
              <li>Time spent on the platform</li>
              <li>Device and browser information</li>
              <li>IP address (for security and fraud prevention)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. Use of YouTube Public Data</h2>
            <p className="text-slate-700 mb-4">
              Sponsiq uses the YouTube Data API to access publicly available information about creator channels 
              and videos. This includes:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>Channel statistics (subscriber count, total views, video count)</li>
              <li>Video metadata (titles, descriptions, tags, thumbnails)</li>
              <li>Public video transcripts (when available)</li>
            </ul>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <p className="text-slate-700 text-sm mb-0">
                <strong>Important:</strong> We do not access private YouTube account data, analytics, or any information 
                that requires OAuth authentication to your YouTube account. All data we analyze is publicly visible 
                to anyone on the YouTube platform.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-700 mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Provide, maintain, and improve our services</li>
              <li>Generate sponsorship intelligence and brand recommendations</li>
              <li>Calculate sponsorship readiness scores</li>
              <li>Send you relevant notifications and updates</li>
              <li>Respond to your comments, questions, and requests</li>
              <li>Monitor and analyze trends, usage, and activities</li>
              <li>Detect, investigate, and prevent fraudulent transactions and abuse</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">4. Data Storage and Protection</h2>
            <p className="text-slate-700 mb-4">
              We implement appropriate technical and organizational measures to protect your personal information:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Data encryption in transit (HTTPS/TLS)</li>
              <li>Secure database storage with access controls</li>
              <li>Regular security audits and monitoring</li>
              <li>Limited employee access to personal data</li>
            </ul>
            <p className="text-slate-700 mt-4">
              We retain your personal data only for as long as necessary to provide our services and fulfill 
              the purposes outlined in this Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">5. Cookie Usage</h2>
            <p className="text-slate-700 mb-4">
              We use cookies and similar tracking technologies to track activity on our Platform and hold certain information:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li><strong>Essential Cookies:</strong> Required for the Platform to function properly</li>
              <li><strong>Authentication Cookies:</strong> Used to keep you logged in</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how you use the Platform</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
            </ul>
            <p className="text-slate-700 mt-4">
              You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. 
              However, if you do not accept cookies, you may not be able to use some portions of our Platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">6. Third-Party Services</h2>
            <p className="text-slate-700 mb-4">We may use third-party services that collect, monitor, and analyze data:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li><strong>YouTube Data API:</strong> To access public creator and video information</li>
              <li><strong>Authentication Providers:</strong> For secure login (Google OAuth)</li>
              <li><strong>Analytics Services:</strong> To understand platform usage</li>
              <li><strong>Cloud Infrastructure:</strong> For secure data storage and processing</li>
            </ul>
            <p className="text-slate-700 mt-4">
              These third-party services have their own privacy policies addressing how they use such information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">7. Your Rights</h2>
            <p className="text-slate-700 mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate personal information</li>
              <li>Request deletion of your personal information</li>
              <li>Object to processing of your personal information</li>
              <li>Request restriction of processing your personal information</li>
              <li>Request transfer of your personal information</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p className="text-slate-700 mt-4">
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:hello.sponsiq@gmail.com" className="text-indigo-600 hover:text-indigo-800">
                hello.sponsiq@gmail.com
              </a>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">8. Children's Privacy</h2>
            <p className="text-slate-700">
              Our Platform is not intended for children under 13 years of age. We do not knowingly collect 
              personal information from children under 13. If you are a parent or guardian and you are aware 
              that your child has provided us with personal information, please contact us.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">9. Changes to This Privacy Policy</h2>
            <p className="text-slate-700">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting 
              the new Privacy Policy on this page and updating the "Last updated" date. You are advised to 
              review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">10. Contact Us</h2>
            <p className="text-slate-700">
              If you have any questions about this Privacy Policy, please contact us at{' '}
              <a href="mailto:hello.sponsiq@gmail.com" className="text-indigo-600 hover:text-indigo-800">
                hello.sponsiq@gmail.com
              </a>
            </p>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-slate-200 flex flex-wrap gap-6 text-sm text-slate-600">
          <Link to="/terms" className="hover:text-indigo-600 transition-colors">Terms of Service</Link>
          <Link to="/" className="hover:text-indigo-600 transition-colors">Back to Home</Link>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
